/**
 * Vulnerability Card Component - Single vulnerability finding with expandable details
 */

import React, { useState, useEffect } from 'react';
import { VulnerabilityFinding, RiskLevel } from '../../types';
import { ChevronDownIcon, SortIcon, GuardrailIcon } from '../../../../components/Icons';
import key from '../../assets/key.png';
import './vulnerabilities.css';

interface VulnerabilityCardProps {
  vulnerability: VulnerabilityFinding;
  isFocused?: boolean;
  onToggle?: () => void;
}

const getSeverityClass = (severity: RiskLevel): string => {
  switch (severity) {
    case 'Critical':
      return 'critical';
    case 'High':
      return 'high';
    case 'Medium':
      return 'medium';
    case 'Low':
      return 'low';
    case 'Info':
      return 'info';
    default:
      return 'medium';
  }
};

const VulnerabilityCard: React.FC<VulnerabilityCardProps> = ({ vulnerability, isFocused, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const severityClass = getSeverityClass(vulnerability.severity);

  // Entries as [framework, mapping]
  const [frameworkEntries, setFrameworkEntries] = useState(
    Object.entries(vulnerability.securityFrameworkMapping)
  );
  const [sortState, setSortState] = useState<{column: 'framework' | 'mapping', direction: 'asc' | 'desc'}>({
    column: 'framework',
    direction: 'desc'
  });

  const sortEntries = (column: 'framework' | 'mapping') => {
    const direction = sortState.direction === 'asc' ? 'desc' : 'asc';
    const sorted = [...frameworkEntries].sort((a, b) => {
      const aVal = column === 'framework' ? a[0] : a[1];
      const bVal = column === 'framework' ? b[0] : b[1];
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFrameworkEntries(sorted);
    setSortState({ column, direction });
  };

  // Helper func to determine icon direction and color
  const getSortIconProps = (column: 'framework' | 'mapping') => {
    const isActive = sortState.column === column;
    return {
      color: isActive ? 'white' : 'grey',
      style: { marginLeft: 4, cursor: 'pointer', verticalAlign: 'middle', height: '16px', width: '16px' },
    };
  };

  useEffect(() => {
    if (isFocused) setIsExpanded(true);
  }, [isFocused]);

  // When toggling, notify parent to clear focus
  const handleToggle = () => {
    let next = !isExpanded;
    setIsExpanded(next);

    if (onToggle) {
      onToggle();
    }
  };

  return (
    <div className={`vuln-card ${isFocused ? 'vuln-card--focused' : ''}`}>
      {/* Severity Bar */}
      <div className={`vuln-card__severity-bar vuln-card__severity-bar--${severityClass}`} />

      {/* Header */}
      <div className="vuln-card__header">
        <div className="vuln-card__title-row">
          <h3 className="vuln-card__title">{vulnerability.name}</h3>
          <span className={`vuln-card__severity-badge vuln-card__severity-badge--${severityClass}`}>
            {vulnerability.severity}
          </span>
        </div>
        <div className="vuln-card__dates">
          Discovered: {vulnerability.discoveredDate} &nbsp;•&nbsp; Updated: {vulnerability.updatedDate}
        </div>
      </div>

      {/* Meta info */}
      <div className="vuln-card__meta">
        <img src={key} alt="" style={{width: '15px', height: '15px'}}/> 
        <span>{vulnerability.affectedTool}</span>
        <span>•</span>
        <span>{vulnerability.category}</span>
      </div>

      {/* Description */}
      <p className="vuln-card__description">{vulnerability.description}</p>

      {/* Toggle Button */}
      <button className="vuln-card__toggle" onClick={handleToggle}>
        <span className="vuln-card__toggle-text">{isExpanded ? 'Hide Details' : 'Show Details'}</span>
        <ChevronDownIcon className={`vuln-card__toggle-icon ${isExpanded ? 'vuln-card__toggle-icon--expanded' : ''}`} />
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="vuln-card__details">
          {/* Security Framework Mapping */}
          <div className="vuln-card__section">
            <h4 className="vuln-card__section-title">Security Framework Mapping</h4>
            <div className="vuln-card__mapping-table">
              <div className="vuln-card__mapping-header">
                <div className="vuln-card__mapping-header-cell vuln-card__mapping-header-cell--framework">
                  Framework
                  <SortIcon {...getSortIconProps('framework')} onClick={() => sortEntries('framework')}/>
                </div>
                <div className="vuln-card__mapping-header-cell vuln-card__mapping-header-cell--mapping">
                  Mapping
                  <SortIcon {...getSortIconProps('mapping')} onClick={() => sortEntries('mapping')} />
                </div>
              </div>
              {frameworkEntries.map(([key, value], index) => (
                <div key={index} className="vuln-card__mapping-row">
                  <div className="vuln-card__mapping-cell vuln-card__mapping-cell--framework">
                    <span className="vuln-card__framework-badge">{key}</span>
                  </div>
                  <div className="vuln-card__mapping-cell vuln-card__mapping-cell--mapping">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Guardrails */}
          {vulnerability.recommendedGuardrails.length > 0 && (
            <div className="vuln-card__section">
              <h4 className="vuln-card__section-title">Recommended Guardrails</h4>
              {vulnerability.recommendedGuardrails.map((guardrail, index) => (
                <div key={index} className="vuln-card__guardrail">
                  <GuardrailIcon className="vuln-card__guardrail-icon" />
                  <span className="vuln-card__guardrail-name">{guardrail}</span>
                  <span className="vuln-card__guardrail-badge">All Input Edges</span>
                </div>
              ))}
            </div>
          )}

          {/* Other Recommendations */}
          {vulnerability.otherRecommendations.length > 0 && (
            <div className="vuln-card__section">
              <h4 className="vuln-card__section-title">Other Recommendations</h4>
              {vulnerability.otherRecommendations.map((recommendation, index) => (
                <div key={index} className="vuln-card__recommendation">
                  {index + 1}. {recommendation}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VulnerabilityCard;
